/**
 * Administrador define uma senha provisória para outro usuário.
 *
 * É o caminho de "esqueci a senha" quando o servidor não envia e-mail, e o
 * de "essa pessoa não pode mais entrar com a senha antiga": a API derruba as
 * sessões abertas junto. A provisória é para ser passada por WhatsApp e
 * trocada pela pessoa em "Alterar senha", no menu.
 */
import { SENHA_MAX, SENHA_MIN, mensagemSenhaInvalida } from '@simpleadm/shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { CampoSenha } from '@/components/CampoSenha';
import { RequisitosSenha } from '@/components/RequisitosSenha';
import { ApiError, apiFetch } from '@/lib/api';


export function RedefinirSenhaUsuarioDialog({
  usuario,
  onClose,
}: {
  usuario: { id: string; fullName: string; email: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState(false);

  const redefinir = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`/users/${usuario.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ password: senha }),
      }),
    onSuccess: () => {
      setFeito(true);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) =>
      setErro(e instanceof ApiError ? e.message : 'Não foi possível redefinir a senha.'),
  });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    const problema = mensagemSenhaInvalida(senha);
    if (problema) return setErro(problema);
    redefinir.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={enviar}
        className="w-full max-w-sm space-y-4 rounded-lg bg-card p-6 text-card-foreground shadow-xl"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <KeyRound className="h-5 w-5" /> Redefinir senha
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          {usuario.fullName} · {usuario.email}
        </p>

        {feito ? (
          <>
            <p className="text-sm">
              Senha redefinida. As sessões abertas dessa pessoa foram encerradas. Passe a senha
              provisória por um canal seguro e oriente a trocar em &quot;Alterar senha&quot;.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <CampoSenha
              label="Senha provisória"
              required
              minLength={SENHA_MIN}
              maxLength={SENHA_MAX}
              autoComplete="off"
              autoFocus
              className="font-mono"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              // Nasce visível: quem define a provisória precisa lê-la para enviar.
              iniciarVisivel
              abaixo={
                <>
                  <span className="text-[10px] text-muted-foreground">
                    Visível de propósito — você vai copiá-la para enviar.
                  </span>
                  <RequisitosSenha senha={senha} />
                </>
              }
            />

            {erro && <p className="text-sm text-destructive">{erro}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={redefinir.isPending}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {redefinir.isPending ? 'Salvando…' : 'Redefinir'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
